/**
 * Expert Consultation Admin Service (spec 017, US-14-GR-16 — Expert Consultation
 * Surfacing, admin half).
 *
 * Every route/shape below CONFIRMED directly by api-dev-gr16 (feat/spec-017-api,
 * ExpertConsultationController + ExpertConsultationContracts.cs — committed on that
 * branch at write time). Everything routes through the existing Ocelot
 * `/api/TaskManager/{everything}` wildcard — no gateway change needed. Auth is the
 * standard bearer token; every endpoint here additionally requires the Admin role
 * server-side (`[Authorize(Roles = "Admin")]`).
 *
 *   GET  /api/TaskManager/consultations?status=&domain=   Admin: all, both filters
 *                                                          optional; status matches the
 *                                                          PascalCase enum name,
 *                                                          case-insensitive server-side
 *   POST /api/TaskManager/consultations/{id}/assign        { expertId } -> ConsultationDto
 *                                                          (400 if expert missing/inactive)
 *   POST /api/TaskManager/consultations/{id}/complete      { notes } -> ConsultationDto.
 *                                                          Works directly from Assigned —
 *                                                          no separate "start" call.
 *                                                          Callable by admin OR the
 *                                                          assigned expert (if they're
 *                                                          also a platform user).
 *   GET  /api/TaskManager/experts                          -> { experts: ExpertDto[], poolEmpty }
 *   POST /api/TaskManager/experts                          -> 201 ExpertDto (400 if email in use)
 *   PUT  /api/TaskManager/experts/{id}                     FULL REPLACE (not a patch) ->
 *                                                          200 ExpertDto, or 404
 *
 * `status` on ConsultationDto is a JsonStringEnumConverter-serialized PascalCase string
 * ("Requested"/"Assigned"/"InProgress"/"Completed"/"Cancelled") — corrected here after
 * the API author's confirmation; this file originally assumed the raw int by analogy
 * with TemplateCategory/Channel (which have no converter). That assumption was wrong
 * for this specific DTO.
 *
 * `domains` travels as a JSON string array on the wire in both directions — corrected
 * from this file's original CSV-join assumption (the Expert.Domains DB column is a
 * CSV, but the DTO itself is an array; the controller does the CSV translation).
 *
 * PUT /experts/{id} is a full replace, not a partial patch — deactivateExpert() takes
 * the whole current ExpertRecord and re-sends every field with isActive flipped, rather
 * than a partial body that would null out the rest server-side.
 */

import { authenticatedFetch } from '../api/authenticatedFetch';

// ---------------------------------------------------------------------------
// Enum — string values mirror the JsonStringEnumConverter-serialized wire format
// (TaskManager.Repository.DatabaseModel.Enums.ConsultationStatus member names)
// ---------------------------------------------------------------------------

export enum ConsultationStatus {
  Requested = 'Requested',
  Assigned = 'Assigned',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
}

export const CONSULTATION_STATUS_LABELS: Record<ConsultationStatus, string> = {
  [ConsultationStatus.Requested]: 'Requested',
  [ConsultationStatus.Assigned]: 'Assigned',
  [ConsultationStatus.InProgress]: 'In Progress',
  [ConsultationStatus.Completed]: 'Completed',
  [ConsultationStatus.Cancelled]: 'Cancelled',
};

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/** Aligned to ExpertDto (ExpertConsultationContracts.cs), spec 017 R7. */
export interface ExpertRecord {
  expertId: string;
  name: string;
  email: string;
  profileId: string | null;
  domains: string[];
  isActive: boolean;
  createdAt: string;
}

export interface CreateExpertInput {
  name: string;
  email: string;
  domains: string[];
  profileId?: string | null;
}

/** PUT /experts/{id} is a full replace — every field required, matching the wire contract. */
export interface UpdateExpertInput {
  name: string;
  email: string;
  profileId: string | null;
  domains: string[];
  isActive: boolean;
}

export interface ExpertPoolResponse {
  poolEmpty: boolean;
  experts: ExpertRecord[];
}

/** Aligned to ConsultationDto (ExpertConsultationContracts.cs). */
export interface ConsultationRecord {
  id: string;
  businessDomain: string;
  additionalContext: string | null;
  status: ConsultationStatus;
  expertId: string | null;
  /** Real API data (ConsultationDto.expertName) — avoids a separate expert lookup on
   * the queue row once assigned. */
  expertName: string | null;
  boardId: string | null;
  cardId: string | null;
  requestedAt: string;
  assignedAt: string | null;
  completedAt: string | null;
  notes: string | null;
  estimatedResponseTime: string | null;
}

export interface ConsultationFilter {
  status?: ConsultationStatus;
  domain?: string;
}

// ---------------------------------------------------------------------------
// Wire DTOs
// ---------------------------------------------------------------------------

interface ExpertWireDto {
  expertId?: string;
  name?: string;
  email?: string;
  profileId?: string | null;
  domains?: string[] | null;
  isActive?: boolean;
  createdAt?: string;
}

interface ConsultationWireDto {
  consultationId?: string;
  businessDomain?: string;
  additionalContext?: string | null;
  status?: string;
  expertId?: string | null;
  expertName?: string | null;
  boardId?: string | null;
  cardId?: string | null;
  requestedAt?: string;
  assignedAt?: string | null;
  completedAt?: string | null;
  notes?: string | null;
  estimatedResponseTime?: string | null;
}

const VALID_STATUS_VALUES = new Set<string>(Object.values(ConsultationStatus));

function normalizeStatus(value: string | undefined): ConsultationStatus {
  if (value != null && VALID_STATUS_VALUES.has(value)) return value as ConsultationStatus;
  return ConsultationStatus.Requested;
}

function normalizeExpert(raw: ExpertWireDto): ExpertRecord {
  return {
    expertId: String(raw.expertId ?? ''),
    name: raw.name ?? '',
    email: raw.email ?? '',
    profileId: raw.profileId ?? null,
    domains: Array.isArray(raw.domains) ? raw.domains.filter((d): d is string => typeof d === 'string') : [],
    isActive: raw.isActive ?? true,
    createdAt: raw.createdAt ?? new Date(0).toISOString(),
  };
}

function normalizeConsultation(raw: ConsultationWireDto): ConsultationRecord {
  return {
    id: String(raw.consultationId ?? ''),
    businessDomain: raw.businessDomain ?? 'General',
    additionalContext: raw.additionalContext ?? null,
    status: normalizeStatus(raw.status),
    expertId: raw.expertId ?? null,
    expertName: raw.expertName ?? null,
    boardId: raw.boardId ?? null,
    cardId: raw.cardId ?? null,
    requestedAt: raw.requestedAt ?? new Date(0).toISOString(),
    assignedAt: raw.assignedAt ?? null,
    completedAt: raw.completedAt ?? null,
    notes: raw.notes ?? null,
    estimatedResponseTime: raw.estimatedResponseTime ?? null,
  };
}

/** Thrown for non-OK responses; carries the HTTP status (e.g. 404) and, when the body
 * parses, the server's own message (e.g. "email already in use", "expert is inactive"). */
export class ExpertConsultationAdminError extends Error {
  readonly status: number;

  constructor(status: number, path: string, serverMessage?: string) {
    super(serverMessage || `Expert consultation admin request failed: HTTP ${status} ${path}`);
    this.name = 'ExpertConsultationAdminError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await authenticatedFetch(path, init);
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { message?: string; error?: string; detail?: string; title?: string }
      | null;
    throw new ExpertConsultationAdminError(
      response.status,
      path,
      body?.message || body?.error || body?.detail || body?.title,
    );
  }
  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Expert pool CRUD (FR-042)
// ---------------------------------------------------------------------------

/** GET /experts — pool list + poolEmpty flag (true when zero ACTIVE experts, regardless
 * of `experts.length` — an inactive-only pool still counts as empty). Also consumed by
 * the user request surface's own getExpertPoolStatus() in the sibling ForSuccessFrontend app. */
export async function getExperts(): Promise<ExpertPoolResponse> {
  const raw = await request<{ experts?: ExpertWireDto[]; poolEmpty?: boolean }>('/api/TaskManager/experts');
  const experts = (raw.experts ?? []).map(normalizeExpert);
  const poolEmpty = raw.poolEmpty ?? experts.filter((e) => e.isActive).length === 0;
  return { poolEmpty, experts };
}

/** POST /experts — add an expert to the pool. `domains` travels as a JSON array. */
export async function createExpert(input: CreateExpertInput): Promise<ExpertRecord> {
  const raw = await request<ExpertWireDto>('/api/TaskManager/experts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return normalizeExpert(raw);
}

/** PUT /experts/{id} — full replace (not a patch). Deactivation is `isActive: false`
 * in this same call, never a delete. */
export async function updateExpert(expertId: string, input: UpdateExpertInput): Promise<ExpertRecord> {
  const raw = await request<ExpertWireDto>(`/api/TaskManager/experts/${encodeURIComponent(expertId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return normalizeExpert(raw);
}

/** Convenience wrapper — deactivate is the only pool-removal path (no hard delete,
 * FR-042). Takes the whole current record because PUT is a full replace: every other
 * field must be re-sent unchanged alongside the isActive flip. */
export async function deactivateExpert(expert: ExpertRecord): Promise<ExpertRecord> {
  return updateExpert(expert.expertId, {
    name: expert.name,
    email: expert.email,
    profileId: expert.profileId,
    domains: expert.domains,
    isActive: false,
  });
}

// ---------------------------------------------------------------------------
// Consultation queue (assign -> complete, FR-042/SC-009)
// ---------------------------------------------------------------------------

/** GET /consultations — admin queue, optionally filtered by status/domain. Bare array response. */
export async function getConsultations(filter: ConsultationFilter = {}): Promise<ConsultationRecord[]> {
  const qs = new URLSearchParams();
  if (filter.status != null) qs.set('status', filter.status);
  if (filter.domain) qs.set('domain', filter.domain);
  const query = qs.toString();
  const raw = await request<ConsultationWireDto[]>(`/api/TaskManager/consultations${query ? `?${query}` : ''}`);
  return (Array.isArray(raw) ? raw : []).map(normalizeConsultation);
}

/** POST /consultations/{id}/assign — moves Requested -> Assigned. 400 if the expert
 * doesn't exist or is inactive (surfaced via ExpertConsultationAdminError.message). */
export async function assignConsultation(id: string, expertId: string): Promise<ConsultationRecord> {
  const raw = await request<ConsultationWireDto>(`/api/TaskManager/consultations/${encodeURIComponent(id)}/assign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expertId }),
  });
  return normalizeConsultation(raw);
}

/** POST /consultations/{id}/complete — moves Assigned/InProgress -> Completed, records
 * outcome notes. Works directly from Assigned; no separate "start" call exists. */
export async function completeConsultation(id: string, notes: string): Promise<ConsultationRecord> {
  const raw = await request<ConsultationWireDto>(`/api/TaskManager/consultations/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes }),
  });
  return normalizeConsultation(raw);
}
